import React, {
  Children,
  isValidElement,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Collapse,
  colors,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";

import { NavigationLink } from "components/navigation-link";
import { hasAdminAccessOrPermissions } from "features/auth/helpers";
import { useSidebar } from "./sidebar-context";
import { isNavItemActive } from "../utils";
import { isComponentTypeOf } from "util/react";
import { Protected } from "features/auth/components/protected";

export function SidebarItem({ sx, ...item }) {
  const { isSidebarOpen, expandedItem, toggleItemExpansion } = useSidebar();
  const initialRenderRef = useRef(true);
  const pathname = useLocation().pathname;
  const user = useSelector((state) => state.auth.user);

  // If title is a React element with props.id (i.e. IntlMessages component), use that id
  // Otherwise use the string title
  const itemTitle = isValidElement(item.title) && item.title.props?.id
    ? item.title.props.id
    : typeof item.title === 'string' ? item.title : null;

  const isExpanded = expandedItem === itemTitle;

  // Check if there will be any rendered children (those with permissions)
  const willRenderAnyChildren = useMemo(
    () =>
      !item.isLink &&
      Children.count(
        Children.map(item.children, (child) => {
          if (!isValidElement(child)) return null;
          // If it's a Protected component, check if it will render its children
          if (
            isComponentTypeOf(child, Protected) &&
            (!user ||
              Object.keys(user).length === 0 ||
              !hasAdminAccessOrPermissions(user, child.props.permissions ?? []))
          ) {
            return null;
          }
          return child;
        })
      ) > 0,
    [item, user]
  );

  // Initialize the expanded state based on active path, but only on initial render
  useEffect(() => {
    if (!initialRenderRef.current || item.isLink || !willRenderAnyChildren || !itemTitle) {
      return;
    }

    if (isNavItemActive(item, pathname)) {
      toggleItemExpansion(itemTitle);
    }

    initialRenderRef.current = false;
  }, [pathname, item, willRenderAnyChildren, toggleItemExpansion, itemTitle]);

  if (item.isLink) {
    return (
      <NavigationLink href={item.href} isExternal={item.isExternal}>
        <NavigationItem
          title={item.title}
          icon={item.icon}
          sx={sx}
          isActive={isNavItemActive(item, pathname)}
        />
      </NavigationLink>
    );
  }

  // Don't render anything if there are no children
  if (!willRenderAnyChildren) return null;

  return (
    <>
      <NavigationItem
        title={item.title}
        icon={item.icon}
        onClick={() => itemTitle && toggleItemExpansion(itemTitle)}
        sx={sx}
        isActive={isNavItemActive(item, pathname)}
        expandIcon={
          <ExpandMoreIcon
            sx={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)" }}
          />
        }
      />
      <Collapse in={isSidebarOpen && isExpanded} timeout="auto" unmountOnExit>
        {item.children}
      </Collapse>
    </>
  );
}

function NavigationItem({
  title,
  icon: Icon,
  sx,
  onClick,
  isActive,
  expandIcon,
}) {
  return (
    <ListItemButton
      sx={sx}
      style={{
        height: "40px",
        color: isActive ? colors.blue[500] : "#242424",
        display: "flex",
        position: "relative",
        cursor: "pointer",
        userSelect: "none",
        verticalAlign: "middle",
        textDecoration: "none",
        flexGrow: 1,
        justifyContent: "flex-start",
        alignItems: "center",
        minWidth: 0,
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "24px",
        paddingRight: "16px",
      }}
      onClick={onClick}
    >
      <ListItemIcon sx={{ color: "inherit" }}>
        <Icon />
      </ListItemIcon>
      <ListItemText
        primary={title}
        primaryTypographyProps={{
          variant: "subtitle1",
          fontWeight: 500,
          style: { color: "inherit" },
        }}
      />
      {expandIcon}
    </ListItemButton>
  );
}
